import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal, Box, Typography } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faQuestionCircle, faTimes, faSpinner } from '@fortawesome/free-solid-svg-icons';
import './knowledgeModal.css';

const FaqModal = ({ open, onClose, knowledgeId }) => {
  const { t } = useTranslation();
  const [title, setTitle] = useState('');
  const [question, setQuestion] = useState('');
  const [answerHtml, setAnswerHtml] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const parseQA = (raw) => {
    if (!raw) return { q: '', a: '' };
    const idx = raw.indexOf('Answer:');
    if (raw.startsWith('Question:') && idx !== -1) {
      const q = raw.slice('Question:'.length, idx).trim();
      const a = raw.slice(idx + 'Answer:'.length).trim();
      return { q, a };
    }
    return { q: '', a: raw.trim() };
  };

  useEffect(() => {
    if (open && knowledgeId) {
      const loadFaq = async () => {
        setLoading(true);
        setError(null);
        try {
          const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}knowledge/${knowledgeId}`);
          if (!res.ok) throw new Error(`Failed to fetch FAQ. Status: ${res.status}`);
          const data = await res.json();
          const { q, a } = parseQA(data.relevant_data);
          setQuestion(q);
          setAnswerHtml(a);
          setTitle(data.description || t('faq_default_title'));
        } catch (err) {
          setError(t('faq_load_failed'));
        } finally {
          setLoading(false);
        }
      };
      loadFaq();
    }
  }, [open, knowledgeId]);

  return (
    <Modal open={open} onClose={onClose}>
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          maxWidth: { xs: '80%', sm: '620px' },
          width: '90%',
          maxHeight: '75vh',
          bgcolor: 'background.paper',
          borderRadius: '10px',
          boxShadow: 24,
          p: 3,
          overflow: 'auto',
          outline: 'none',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Header */}
        <div className="knowledge-modal-header">
          <h2 className="knowledge-modal-header-title">
            <FontAwesomeIcon
              icon={faQuestionCircle}
              className="knowledge-modal-type-icon"
            />
            {title}
          </h2>
          <button className="knowledge-modal-close" onClick={onClose}>
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
            <FontAwesomeIcon icon={faSpinner} spin size="2x" color="#5616ea" />
          </Box>
        ) : error ? (
          <Typography color="error">{error}</Typography>
        ) : (
          <Box>
            <Typography sx={{ fontWeight: 700, color: '#5616ea', fontSize: '0.9rem' }}>
              {t('faq_question_heading')}
            </Typography>
            <Box
              sx={{
                whiteSpace: 'pre-wrap',
                backgroundColor: '#fafafa',
                padding: '12px',
                borderRadius: '10px',
                border: '1px solid #ebebeb',
                mt: 1,
                mb: 2,
                fontSize: '0.9rem',
                lineHeight: 1.75,
              }}
            >
              {question || t('faq_no_question')}
            </Box>

            <Typography sx={{ mb: 1, fontWeight: 700, color: '#5616ea', fontSize: '0.9rem' }}>
              {t('faq_answer_heading')}
            </Typography>
            <Typography
              component="div"
              sx={{
                whiteSpace: 'pre-wrap',
                backgroundColor: '#fafafa',
                padding: '12px',
                borderRadius: '10px',
                border: '1px solid #ebebeb',
                fontSize: '0.9rem',
                lineHeight: 1.75,
                '& > *:first-of-type': { marginTop: 0 },
                '& > *:last-child': { marginBottom: 0 },
              }}
              dangerouslySetInnerHTML={{ __html: answerHtml || '' }}
            />
          </Box>
        )}
      </Box>
    </Modal>
  );
};

export default FaqModal;
